import React from "react"
import { useState } from "react"
import { Card, Container, Row, Col, Button, Badge, Form, Modal } from "react-bootstrap"
import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaFileAlt,
  FaSearch,
  FaFilter,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaStethoscope,
} from "react-icons/fa"

export default function PatientAppointments() {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [showModal, setShowModal] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)

  const appointments = [
    {
      id: 1,
      date: "2024-02-05",
      time: "11:15 AM",
      doctor: "Dr. Emily Davis",
      specialty: "Dermatology",
      status: "Scheduled",
      location: "Skin Care Center, Room 302",
      phone: "+1 (555) 234-5678",
      reason: "Annual skin check-up",
      duration: "30 min",
      notes: "Bring list of current medications",
    },
    {
      id: 2,
      date: "2024-02-12",
      time: "9:00 AM",
      doctor: "Dr. Robert Wilson",
      specialty: "Orthopedics",
      status: "Scheduled",
      location: "Orthopedic Wing, Room 150",
      phone: "+1 (555) 345-6789",
      reason: "Follow-up for knee pain",
      duration: "45 min",
      notes: "X-rays may be required",
    },
    {
      id: 3,
      date: "2024-01-22",
      time: "2:30 PM",
      doctor: "Dr. Michael Chen",
      specialty: "General Medicine",
      status: "Completed",
      location: "Main Clinic, Room 105",
      phone: "+1 (555) 456-7890",
      reason: "Annual physical examination",
      duration: "60 min",
      notes: "Blood work completed",
    },
    {
      id: 4,
      date: "2024-01-15",
      time: "10:00 AM",
      doctor: "Dr. Sarah Johnson",
      specialty: "Cardiology",
      status: "Completed",
      location: "Heart Center, Room 201",
      phone: "+1 (555) 567-8901",
      reason: "Cardiac consultation",
      duration: "45 min",
      notes: "EKG and stress test performed",
    },
    {
      id: 5,
      date: "2024-01-08",
      time: "3:45 PM",
      doctor: "Dr. Lisa Martinez",
      specialty: "Endocrinology",
      status: "Cancelled",
      location: "Diabetes Center, Room 250",
      phone: "+1 (555) 678-9012",
      reason: "Diabetes management",
      duration: "30 min",
      notes: "Rescheduled due to weather",
    },
    {
      id: 6,
      date: "2024-02-20",
      time: "1:00 PM",
      doctor: "Dr. James Thompson",
      specialty: "Ophthalmology",
      status: "Scheduled",
      location: "Eye Care Center, Room 180",
      phone: "+1 (555) 789-0123",
      reason: "Eye examination",
      duration: "30 min",
      notes: "Bring current glasses",
    },
  ]

  const filteredAppointments = appointments.filter((appointment) => {
    const matchesSearch =
      appointment.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.reason.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "all" || appointment.status.toLowerCase() === statusFilter.toLowerCase()

    return matchesSearch && matchesStatus
  })

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <FaCheckCircle className="text-success" />
      case "Scheduled":
        return <FaClock style={{ color: "#0891b2" }} />
      case "Cancelled":
        return <FaTimesCircle className="text-danger" />
      default:
        return <FaExclamationTriangle className="text-warning" />
    }
  }

  const upcomingAppointments = appointments.filter((apt) => apt.status === "Scheduled").length
  const completedAppointments = appointments.filter((apt) => apt.status === "Completed").length
  const cancelledAppointments = appointments.filter((apt) => apt.status === "Cancelled").length

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment)
    setShowModal(true)
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Header */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
            }}
          >
            {/* Background Pattern */}
            <div className="position-absolute w-100 h-100 top-0 start-0">
              <div
                className="position-absolute rounded-circle opacity-10"
                style={{
                  width: "200px",
                  height: "200px",
                  background: "white",
                  top: "-50px",
                  right: "-50px",
                }}
              ></div>
            </div>

            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaCalendarAlt className="me-2" />
                    Appointments
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">My Appointments</h1>
                <p className="fs-5 opacity-90 mb-4">
                  Manage your Aidify appointments and view your medical history
                </p>
                <Button
                  variant="light"
                  className="px-4 py-2 rounded-pill fw-medium"
                  style={{
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <FaPlus className="me-2" style={{ color: "#0891b2" }} />
                  <span style={{ color: "#0891b2" }}>Book New Appointment</span>
                </Button>
              </Col>
              <Col md={4} className="text-end">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "120px",
                    height: "120px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FaCalendarAlt size={48} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-5">
          <Col md={3}>
            <Card
              className="border-0 rounded-4 h-100"
              style={{
                boxShadow: "0 10px 40px rgba(8, 145, 178, 0.15)",
                background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
              }}
            >
              <Card.Body className="p-4 text-white text-center">
                <FaCalendarAlt size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{upcomingAppointments}</div>
                <div className="opacity-90">Upcoming</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card
              className="border-0 rounded-4 h-100"
              style={{
                boxShadow: "0 10px 40px rgba(5, 150, 105, 0.15)",
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              }}
            >
              <Card.Body className="p-4 text-white text-center">
                <FaCheckCircle size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{completedAppointments}</div>
                <div className="opacity-90">Completed</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card
              className="border-0 rounded-4 h-100"
              style={{
                boxShadow: "0 10px 40px rgba(220, 38, 38, 0.15)",
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
              }}
            >
              <Card.Body className="p-4 text-white text-center">
                <FaTimesCircle size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{cancelledAppointments}</div>
                <div className="opacity-90">Cancelled</div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card
              className="border-0 rounded-4 h-100"
              style={{
                boxShadow: "0 10px 40px rgba(37, 99, 235, 0.15)",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              }}
            >
              <Card.Body className="p-4 text-white text-center">
                <FaFileAlt size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{appointments.length}</div>
                <div className="opacity-90">Total</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="border-0 rounded-4 mb-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
          <Card.Header className="bg-transparent border-0 p-4">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                  color: "white",
                }}
              >
                <FaFilter size={18} />
              </div>
              <div>
                <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                  Filter Appointments
                </h5>
                <p className="text-muted mb-0">Search and filter your appointments</p>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={8}>
                <div className="position-relative">
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <Form.Control
                    type="text"
                    placeholder="Search by doctor, specialty, or reason..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5 rounded-3 border-0"
                    style={{
                      backgroundColor: "#f0fdfa",
                      boxShadow: "0 0 0 1px #a7f3d0",
                    }}
                  />
                </div>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-3 border-0"
                  style={{
                    backgroundColor: "#f0fdfa",
                    boxShadow: "0 0 0 1px #a7f3d0",
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Appointments List */}
        <Card className="border-0 rounded-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
          <Card.Header className="bg-transparent border-0 p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                  Appointment History
                </h5>
                <p className="text-muted mb-0">{filteredAppointments.length} appointment(s) found</p>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {filteredAppointments.length > 0 ? (
              <div className="d-flex flex-column">
                {filteredAppointments.map((appointment, index) => (
                  <div
                    key={appointment.id}
                    className={`p-4 d-flex justify-content-between align-items-center ${
                      index !== filteredAppointments.length - 1 ? "border-bottom" : ""
                    }`}
                    style={{
                      borderColor: "#a7f3d0",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f0fdfa"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle me-4 d-flex align-items-center justify-content-center"
                        style={{
                          width: "60px",
                          height: "60px",
                          background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                          color: "white",
                        }}
                      >
                        <FaStethoscope size={24} />
                      </div>
                      <div>
                        <div className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                          {appointment.doctor}
                        </div>
                        <div className="text-muted mb-1">{appointment.specialty}</div>
                        <div className="d-flex align-items-center text-muted small">
                          <FaCalendarAlt className="me-1" size={12} />
                          {appointment.date} at {appointment.time}
                          <span className="mx-2">•</span>
                          <FaClock className="me-1" size={12} />
                          {appointment.duration}
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <Badge
                        className="mb-2 d-flex align-items-center gap-1 w-fit ms-auto"
                        style={{
                          background:
                            appointment.status === "Completed"
                              ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                              : appointment.status === "Scheduled"
                                ? "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)"
                                : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                          border: "none",
                        }}
                      >
                        {getStatusIcon(appointment.status)}
                        {appointment.status}
                      </Badge>
                      <div className="d-flex align-items-center text-muted small mb-2">
                        <FaMapMarkerAlt className="me-1" size={12} />
                        {appointment.location}
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="rounded-pill"
                          onClick={() => handleViewDetails(appointment)}
                          style={{ borderColor: "#0891b2", color: "#0891b2" }}
                        >
                          View Details
                        </Button>
                        {appointment.status === "Scheduled" && (
                          <>
                            <Button size="sm" variant="outline-warning" className="rounded-pill">
                              Reschedule
                            </Button>
                            <Button size="sm" variant="outline-danger" className="rounded-pill">
                              Cancel
                            </Button>
                          </>
                        )}
                        {appointment.status === "Completed" && (
                          <Button size="sm" variant="outline-success" className="rounded-pill">
                            <FaFileAlt className="me-1" size={12} />
                            Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <FaCalendarAlt size={64} className="text-muted mb-3" />
                <h5 className="text-muted mb-2">No appointments found</h5>
                <p className="text-muted mb-4">Try adjusting your search criteria or book a new appointment</p>
                <Button
                  className="px-4 py-2 rounded-pill fw-medium border-0"
                  style={{
                    background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                  }}
                >
                  <FaPlus className="me-2" />
                  Book New Appointment
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Appointment Details Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title>Appointment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedAppointment && (
              <Row className="g-4">
                <Col md={6}>
                  <h6 className="fw-bold mb-3" style={{ color: "#0891b2" }}>
                    Appointment Information
                  </h6>
                  <div className="mb-3">
                    <strong>Date:</strong> {selectedAppointment.date}
                  </div>
                  <div className="mb-3">
                    <strong>Time:</strong> {selectedAppointment.time}
                  </div>
                  <div className="mb-3">
                    <strong>Duration:</strong> {selectedAppointment.duration}
                  </div>
                  <div className="mb-3">
                    <strong>Reason:</strong> {selectedAppointment.reason}
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>{" "}
                    <Badge
                      style={{
                        background:
                          selectedAppointment.status === "Completed"
                            ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                            : selectedAppointment.status === "Scheduled"
                              ? "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)"
                              : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                        border: "none",
                      }}
                    >
                      {selectedAppointment.status}
                    </Badge>
                  </div>
                </Col>
                <Col md={6}>
                  <h6 className="fw-bold mb-3" style={{ color: "#0891b2" }}>
                    Doctor & Location
                  </h6>
                  <div className="mb-3">
                    <strong>Doctor:</strong> {selectedAppointment.doctor}
                  </div>
                  <div className="mb-3">
                    <strong>Specialty:</strong> {selectedAppointment.specialty}
                  </div>
                  <div className="mb-3">
                    <strong>Phone:</strong> {selectedAppointment.phone}
                  </div>
                  <div className="mb-3">
                    <strong>Location:</strong> {selectedAppointment.location}
                  </div>
                  {selectedAppointment.notes && (
                    <div className="mb-3">
                      <strong>Notes:</strong> {selectedAppointment.notes}
                    </div>
                  )}
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            {selectedAppointment?.status === "Scheduled" && (
              <Button
                style={{
                  background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                  border: "none",
                }}
              >
                Edit Appointment
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  )
}
